import React, { useEffect, useRef, useState } from "react";
import { Button, Box, Card, CircularProgress, Dialog, DialogContent, DialogTitle, Grid2, IconButton, Link, Stack, Tooltip, Typography, DialogActions, useTheme } from "@mui/material";
import { useOnMobile } from "@core/hooks/useOnMobile";
import Markdown from "react-markdown";
import MarkdownRenderer from "@/domains/core/components/MarkdownRenderer";
import { icon } from "@/app/style/icons";
import { useParams } from "react-router-dom";
import { ContentNode, Message, Mutation, Query } from "@/app/graphql/codegen";
import { DELETE_MESSAGE } from "@chat/graphql/mutations/deleteMessage";
import { REMOVE_ATTACHMENT } from "@chat/graphql/mutations/removeAttachment";
import { useMutation, useQuery } from "@apollo/client";
import { FETCH_CHAT } from "@chat/graphql/queries/fetchChat";
import { FETCH_MESSAGES_FOR_CHAT } from "@chat/graphql/queries/fetchMessagesForChat";
import { FETCH_CONTEXT_FOR_MESSAGE } from "@chat/graphql/queries/fetchContextForMessage";
import { FETCH_DOCUMENT_ID_BY_REFERENCE } from "@documents/graphql/queries/fetchDocumentIdByReference";
import { EDIT_CHAT_SETTINGS } from "@company/graphql/mutations/updateChatSettings";
import DocChip from "@chat/components/DocChip";
import DocumentDialog from "@chat/components/DocumentDialog";
import { useDispatch } from "react-redux";
import { triggerNotification } from "@store/slices/notification";
import { setInputValue } from "@store/slices/chat";
import hex2rgba from "hex2rgba";
import { useSelectContentNodeInChat } from "@chat/hooks/useSelectContentNodeInChat";
import { CHECK_DOCUMENT_EXISTS } from "@documents/graphql/queries/checkDocumentExists";
import { useEnterKeyDialog } from "@core/hooks/useEnterKeyDialog";
import LogoNew from "@/domains/core/components/LogoNew";
import LogoImageOnly from "@/domains/core/components/LogoImageOnly";
import AttachmentPreview from "@chat/components/AttachmentPreview";

/**
 * Regulärer Ausdruck zum Erkennen von Referenzen in Texten.
 * Format: [Referenz;zusatzInfo;Seite]
 * Dabei sind beide Semikolon-Teile optional.
 */
const REFERENCE_PATTERN = /\[(.*?)(?:;(.*?))?(?:;(.*?))?\]/g;

interface ChatMessageProps {
    message: Message | (Partial<Message> & { 
        id: string;
        // Allows for dummy messages with manually constructed attachments
        attachments?: Array<{
            id?: string;
            fileName: string;
            fileSize: number;
            tokens: number;
        }>;
    });
    isDummyMessage?: boolean;
    isGenerating?: boolean;
    refetchMessages?: () => void;
    hideCopy?: boolean;
    hideDelete?: boolean;
    isSyntheticMessage?: boolean;
}

// Struktur für die Darstellung von extrahierten Referenzen
interface ReferenceData {
    fullMatch: string;
    reference: string;
    additionalInfo: string;
    page: string;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, isDummyMessage = false, isGenerating, refetchMessages, hideCopy, hideDelete, isSyntheticMessage }) => {
    const theme = useTheme();
    const onMobile = useOnMobile();
    const dispatch = useDispatch();
    const isBot = message.type === "BOT";
    const [documentRefs, setDocumentRefs] = useState<ContentNode[]>([]);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [nonExistentDocuments, setNonExistentDocuments] = useState<string[]>([]);
    
    // Add ref to track retry attempts for context fetching
    const retryCountRef = useRef(0);
    const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { id: chatId } = useParams<{ id: string }>();

    const { data, refetch: refetchContext } = useQuery<Pick<Query, "contextForMessage">>(FETCH_CONTEXT_FOR_MESSAGE, {
        variables: { messageId: message.id },
        skip: !message.id || isDummyMessage || isSyntheticMessage,
        fetchPolicy: "cache-and-network",
        nextFetchPolicy: "network-only", // Always check network for updates instead of cache-first
        notifyOnNetworkStatusChange: true, // Get notified about network status changes
        onCompleted: (data) => {
            setDocumentRefs((data?.contextForMessage?.contentNodes as ContentNode[]) || []);
        },
    });
    const context = data?.contextForMessage;

    const isGraphMessage = context?.graphEnabled;

    const [deleteMessageMutation] = useMutation<Pick<Mutation, "deleteMessage">, { id: string }>(DELETE_MESSAGE);
    const [removeAttachmentMutation] = useMutation(REMOVE_ATTACHMENT);

    // Add mutations for modifying chat context and settings
    const { selectContentNode, isContextFull, isContentNodeSelected } = useSelectContentNodeInChat(chatId, {
        additionalRefetchQueries: [{ query: FETCH_CHAT, variables: { id: chatId } }],
    });

    const [editChatSettingsMutation] = useMutation<Pick<Mutation, "editChatSettings">>(EDIT_CHAT_SETTINGS);
    const { refetch: refetchDocumentIdByReference } = useQuery<Pick<Query, "officialDocumentByReference">>(FETCH_DOCUMENT_ID_BY_REFERENCE, { skip: true });
    const { refetch: checkDocumentExists } = useQuery(CHECK_DOCUMENT_EXISTS, { skip: true });

    const deleteMessage = async (id: string) => {
        const result = await deleteMessageMutation({
            variables: { id },
            refetchQueries: [
                { query: FETCH_CHAT, variables: { id: chatId } },
                { query: FETCH_MESSAGES_FOR_CHAT, variables: { id: chatId } },
            ],
        });
        return result.data?.deleteMessage;
    };

    const messageIsGenerating = isBot && message.content === "";

    // Consolidated useEffect for handling message generation and context refetching
    useEffect(() => {
        if (messageIsGenerating && !isDummyMessage && !isSyntheticMessage) {
            // Poll for message updates while generating (only for real messages)
            const interval = setInterval(async () => {
                refetchMessages && refetchMessages();
                refetchContext && refetchContext();
            }, 1000); // Increased from 500ms to 1000ms to reduce aggressive polling
            
            return () => clearInterval(interval);
        } else if (message.content && isBot && !isDummyMessage && !isSyntheticMessage) {
            // Message generation completed, ensure context is fetched (only for real messages)
            // Add a small delay to account for backend processing time
            const timeoutId = setTimeout(() => {
                refetchContext && refetchContext();
            }, 100);
            
            return () => clearTimeout(timeoutId);
        }
    }, [messageIsGenerating, message.content, isBot, refetchMessages, refetchContext, isDummyMessage, isSyntheticMessage]);

    // Enhanced retry logic for context fetching with exponential backoff
    useEffect(() => {
        if (message.content && isBot && !messageIsGenerating && !isDummyMessage && !isSyntheticMessage && (!context || (!context.contentNodes?.length && !context.graphEnabled && !context.companyContextEnabled))) {
            // Reset retry count when message content or generation state changes
            retryCountRef.current = 0;
            
            const maxRetries = 5;
            
            const retryFetch = () => {
                if (retryCountRef.current < maxRetries && refetchContext) {
                    const retryDelay = Math.min(1000 * Math.pow(2, retryCountRef.current), 5000); // Exponential backoff, max 5s
                    retryCountRef.current++;
                    
                    retryTimeoutRef.current = setTimeout(() => {
                        refetchContext();
                        retryFetch(); // Schedule next retry
                    }, retryDelay);
                }
            };
            
            // Start the retry process
            retryFetch();
            
            return () => {
                // Clean up timeout on unmount or dependency change
                if (retryTimeoutRef.current) {
                    clearTimeout(retryTimeoutRef.current);
                    retryTimeoutRef.current = null;
                }
            };
        } else {
            // Clean up any existing retry timeout when conditions are no longer met
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
            retryCountRef.current = 0;
        }
    }, [message.content, isBot, messageIsGenerating, context, refetchContext, isDummyMessage, isSyntheticMessage]);

    // Cleanup effect to prevent memory leaks on component unmount
    useEffect(() => {
        return () => {
            if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
            }
        };
    }, []);

    const handleDelete = async () => {
        if (isSyntheticMessage) {
            dispatch(triggerNotification({ 
                content: "Dies ist keine echte Nachricht und wird verschwinden, sobald du deine erste Nachricht sendest.", 
                type: "info", 
                autoHideDuration: 4000 
            }));
            return;
        }
        
        try {
            console.log("Deleting message:", message.id);
            await deleteMessage(message.id);
        } catch (error) {
            console.error("Failed to delete message:", error);
        }
    };

    const handleRemoveAttachment = async (attachmentId: string) => {
        try {
            await removeAttachmentMutation({
                variables: { attachmentId },
                refetchQueries: [
                    { query: FETCH_MESSAGES_FOR_CHAT, variables: { id: chatId } },
                    { query: FETCH_CONTEXT_FOR_MESSAGE, variables: { messageId: message.id } },
                ],
            });
            dispatch(triggerNotification({ 
                content: "Anhang entfernt", 
                type: "success", 
                autoHideDuration: 2000 
            }));
        } catch (error) {
            console.error("Failed to remove attachment:", error);
            dispatch(triggerNotification({ 
                content: "Fehler beim Entfernen des Anhangs", 
                type: "error", 
                autoHideDuration: 3000 
            }));
        }
    };

    // Enable Enter key for delete confirmation dialog
    useEnterKeyDialog({
        isOpen: isDeleteDialogOpen,
        onConfirm: handleDelete,
        disabled: isGenerating,
    });

    const handleCopy = () => {
        if (message.content) {
            navigator.clipboard.writeText(message.content);

            dispatch(triggerNotification({ content: "Nachricht kopiert", type: "success", autoHideDuration: 2000 }));
        }
    };

    const [openedDocument, setOpenedDocument] = useState<{
        reference: string;
        additionalInfo: string;
        page: string;
    } | null>(null);

    /**
     * Öffnet den Dokumentdialog mit der angeklickten Referenz
     */
    const handleReferenceClick = (reference: string, additionalInfo: string, page: string) => {
        console.log("Reference clicked:", { reference, additionalInfo, page });
        setOpenedDocument({
            reference,
            additionalInfo,
            page,
        });
    };

    /**
     * Selects a document reference, configures the chat context, and inserts a static message
     */
    const selectReferenceClick = async (reference: string, additionalInfo: string, page: string) => {
        console.log("Reference clicked to select:", { reference, additionalInfo, page });

        if (!chatId) {
            console.error("No chat ID available");
            return;
        }

        try {
            // 1. Find the content node for the document reference
            const documentResult = await refetchDocumentIdByReference({ reference });
            const document = documentResult.data?.officialDocumentByReference;

            if (!document?.rootContentNode?.id) {
                dispatch(
                    triggerNotification({
                        content: `Dokument nicht gefunden: ${reference}`,
                        type: "error",
                        autoHideDuration: 3000,
                    }),
                );
                return;
            }

            const contentNodeId = document.rootContentNode.id;

            // 2. Turn off graph/SmartSearch mode
            await editChatSettingsMutation({
                variables: {
                    id: chatId,
                    graphEnabled: false,
                },
            });

            // 3. Clear existing context by using the clearContextSelection mutation
            /*
            await clearContextMutation({
                variables: {
                    chatId,
                },
                refetchQueries: [{ query: FETCH_CONTEXT_FOR_CHAT, variables: { chatId } }],
            });
            */

            // 4. Add clicked reference to context (the hook will handle already-selected and context-full cases)
            await selectContentNode(contentNodeId, true, `${reference} wurde ausgewählt`);

            // 5. Insert static message as input (only if the document was actually selected)
            if (!isContentNodeSelected(contentNodeId)) {
                const messageText = "Bitte beantworte meine Frage mit dem angewählten Dokument.";
                dispatch(setInputValue({ chatId, value: messageText }));
            }
        } catch (error) {
            console.error("Error selecting reference:", error);
            dispatch(
                triggerNotification({
                    content: "Fehler beim Auswählen des Dokuments",
                    type: "error",
                    autoHideDuration: 3000,
                }),
            );
        }
    };

    useEffect(() => {
        if (message.content && isBot) {
            const matches = [...message.content.matchAll(REFERENCE_PATTERN)];

            if (matches.length > 0) {
                console.log("References found in message:");
                matches.forEach((match, index) => {
                    console.log(`Reference #${index + 1}:`, {
                        full: match[0],
                        reference: match[1].trim(),
                        additionalInfo: match[2]?.trim() || "",
                        page: match[3]?.trim() || "1",
                    });
                });
            }
        }
    }, [message.content, isBot]);

    const showChips = documentRefs.length > 0 || context?.graphEnabled || context?.companyContextEnabled;

    // Helper to decide if a text snippet is safe to render inline (no block-level markdown)
    const isInlineSafe = (text: string): boolean => {
        if (!text) return true;
        // Avoid inline if the snippet contains line breaks or table-like structures
        if (text.includes("\n")) return false;
        // Basic guard against accidental HTML tables
        if (/^\s*<table/i.test(text)) return false;
        return true;
    };

    /**
     * Verarbeitet einen Textabschnitt vor einer Referenz und fügt ggf. einen Punkt hinzu
     * @param content - Der gesamte Nachrichteninhalt
     * @param lastIndex - Der Startindex für diesen Abschnitt
     * @param matchIndex - Der Index der gefundenen Referenz
     * @param isPeriodAfterReference - Ob ein Punkt nach der Referenz folgt
     * @param contentParts - Array mit den bereits verarbeiteten Inhalten
     * @returns Neuer lastIndex nach der Verarbeitung
     */
    const processTextBeforeReference = (content: string, lastIndex: number, matchIndex: number, isPeriodAfterReference: boolean, contentParts: JSX.Element[]): number => {
        if (matchIndex > lastIndex) {
            // Text vor der Referenz verarbeiten
            let textBefore = content.substring(lastIndex, matchIndex);

            // Falls ein Punkt nach der Referenz folgt, diesen zum Text davor hinzufügen
            if (isPeriodAfterReference) {
                // Leerzeichen am Ende entfernen, wenn vorhanden
                if (textBefore.endsWith(" ")) {
                    textBefore = textBefore.trimEnd();
                }
                textBefore += ".";
            }

            const inlineSafe = isInlineSafe(textBefore);
            contentParts.push(
                <MarkdownRenderer
                    key={`text-${lastIndex}`}
                    variant={inlineSafe ? "inline" : "block"}
                    align="left"
                    isUserMessage={!isBot}
                >
                    {textBefore}
                </MarkdownRenderer>,
            );
        } else if (isPeriodAfterReference) {
            // Wenn kein Text vor der Referenz ist, aber ein Punkt folgt
            contentParts.push(
                <MarkdownRenderer key={`text-${lastIndex}`} variant="inline" align="left" isUserMessage={!isBot}>
                    .
                </MarkdownRenderer>,
            );
        }

        return lastIndex;
    };

    /**
     * Korrigiert Referenzdaten, wenn Seitenzahlen im falschen Feld sind
     */
    const normalizeReferenceData = (
        reference: string,
        additionalInfo: string,
        page: string,
    ): {
        reference: string;
        additionalInfo: string;
        page: string;
    } => {
        // Erweiterte Normalisierung der Referenz
        // Entferne alle Arten von Whitespace (Leerzeichen, Tabs, Zeilenumbrüche, Nicht-brechende Leerzeichen)
        let cleanReference = reference.replace(/[\s\u00A0\t\n\r]+/g, "");
        
        // Normale Verarbeitung für weitere Felder
        let correctPage = page?.trim() || "1";
        let correctAdditionalInfo = additionalInfo?.trim() || "";

        // Wenn additionalInfo mit einem Seitenmuster beginnt (z.B. "S. 40"),
        // ist es wahrscheinlich die Seitenangabe
        if (correctAdditionalInfo && correctAdditionalInfo.match(/^S\.?\s*\d+/i)) {
            correctPage = correctAdditionalInfo;
            correctAdditionalInfo = "";
        }

        return {
            reference: cleanReference,
            additionalInfo: correctAdditionalInfo,
            page: correctPage,
        };
    };

    /**
     * Erstellt ein Referenz-Link-Element
     */
    const createReferenceLink = (referenceData: ReferenceData, matchIndex: number): JSX.Element => {
        const { fullMatch, reference, additionalInfo, page } = referenceData;
        const normalizedData = normalizeReferenceData(reference, additionalInfo, page);

        let displayText = normalizedData.reference;
        
        if (normalizedData.additionalInfo) {
            displayText += ` - ${normalizedData.additionalInfo}`;
        }
        
        if (normalizedData.page && normalizedData.page !== "1") {
            // Formatierung beibehalten
            displayText += ` - ${normalizedData.page}`;
        }

        return (
            <Link
                key={`ref-${matchIndex}`}
                component="button"
                onClick={() => {
                    console.log("Corrected reference params:", normalizedData);
                    handleReferenceClick(normalizedData.reference, normalizedData.additionalInfo, normalizedData.page);
                }}
                sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    fontWeight: "medium",
                    backgroundColor: "rgba(25, 118, 210, 0.08)",
                    padding: "0 4px",
                    borderRadius: "4px",
                    display: "inline-block",
                    mx: 0.5,
                    userSelect: "text",
                }}
                title={`Referenz: ${fullMatch}`}
            >
                {displayText}
            </Link>
        );
    };

    /**
     * Verarbeitet den Text und extrahiert alle Referenzen
     * @param content Der zu verarbeitende Text
     * @returns Array mit formatierten Textteilen und Referenzlinks
     */
    const processMessageContent = (content: string): JSX.Element[] => {
        // In Graph/SmartSearch-Modus zeigen wir den gesamten Text ohne Referenz-Links an
        if (isBot && context?.graphEnabled) {
            // Entferne alle Referenzmuster in eckigen Klammern aus dem Text
            const cleanedContent = content.replace(REFERENCE_PATTERN, (match, reference) => {
                return reference.trim();
            });
            return [
                <MarkdownRenderer key="text-content" align="left" isUserMessage={!isBot}>
                    {cleanedContent}
                </MarkdownRenderer>,
            ];
        }

        const contentParts: JSX.Element[] = [];
        let lastIndex = 0;
        let match;

        // Alle Referenzmuster finden und verarbeiten
        while ((match = REFERENCE_PATTERN.exec(content)) !== null) {
            // Referenzdaten extrahieren
            const [fullMatch, reference, additionalInfo, page] = match;
            const referenceData: ReferenceData = { fullMatch, reference, additionalInfo, page };

            // Prüfen, ob nach der Referenz ein Punkt folgt
            const nextCharIndex = match.index + fullMatch.length;
            const nextChar = content.charAt(nextCharIndex);
            const isPeriodAfterReference = nextChar === ".";

            // Text vor der Referenz verarbeiten
            processTextBeforeReference(content, lastIndex, match.index, isPeriodAfterReference, contentParts);

            // Referenzlink erstellen und hinzufügen
            contentParts.push(createReferenceLink(referenceData, match.index));

            // Position aktualisieren (Punkt überspringen, wenn vorhanden)
            lastIndex = match.index + fullMatch.length + (isPeriodAfterReference ? 1 : 0);
        }

        // Restlichen Text nach der letzten Referenz hinzufügen
        if (lastIndex < content.length) {
            contentParts.push(
                <MarkdownRenderer key={`text-${lastIndex}`} align="left" isUserMessage={!isBot}>
                    {content.substring(lastIndex)}
                </MarkdownRenderer>,
            );
        }

        return contentParts;
    };

    /**
     * Checks if a document reference exists in the system
     */
    const checkIfDocumentExists = async (reference: string): Promise<boolean> => {
        try {
            // Normalize the reference first to match our backend lookup
            const { reference: normalizedRef } = normalizeReferenceData(reference, "", "1");
            
            // First try with the full reference
            const { data } = await checkDocumentExists({ reference: normalizedRef });
            if (data?.documentExists?.id) {
                return true;
            }
            
            // If not found and reference contains underscore, try with parent document ID
            if (normalizedRef.includes('_')) {
                const parentRef = normalizedRef.split('_')[0];
                if (parentRef.length > 0) {
                    const parentResult = await checkDocumentExists({ reference: parentRef });
                    return !!parentResult.data?.documentExists?.id;
                }
            }
            
            return false;
        } catch (error) {
            console.error(`Error checking if document exists: ${reference}`, error);
            return false;
        }
    };

    /**
     * Checks all extracted references and updates the nonExistentDocuments state
     */
    const checkReferencedDocuments = async (content: string) => {
        if (!content || !isBot || !context?.graphEnabled) return;

        const references = [...content.matchAll(REFERENCE_PATTERN)];
        if (references.length === 0) return;

        // Get unique references
        const uniqueRefs = Array.from(
            new Set(
                references.map(match => {
                    const reference = match[1].trim();
                    const additionalInfo = match[2]?.trim() || "";
                    const page = match[3]?.trim() || "1";
                    const { reference: normalizedRef } = normalizeReferenceData(reference, additionalInfo, page);
                    return normalizedRef;
                })
            )
        );

        const nonExistentRefs: string[] = [];
        
        // Check each reference
        for (const ref of uniqueRefs) {
            const exists = await checkIfDocumentExists(ref);
            if (!exists) {
                nonExistentRefs.push(ref);
            }
        }
        
        console.log("nonExistentRefs", nonExistentRefs);
        setNonExistentDocuments(nonExistentRefs);
    };

    // Check referenced documents when the message content changes
    useEffect(() => {
        if (message.content && isBot && context?.graphEnabled) {
            checkReferencedDocuments(message.content);
        }
    }, [message.content, isBot, context?.graphEnabled]);

    const MessageContextBar = () => {
        // For bot messages: Read attachments from TaskRecord metadata (historical record)
        // This ensures the generation info persists even if attachments are later deleted
        const usedAttachments = (message as any).taskRecord?.metadata?.usedAttachments || [];
        
        // Determine if we should show context chips
        const showContextChips = showChips || usedAttachments.length > 0;
        
        return (
            <Stack direction={"row"} justifyContent={"space-between"}>
                <Typography variant={"body2"} fontSize={"0.8rem"} sx={{ px: 1.5, py: 0.5 }} component={"div"}>
                    {showContextChips && (
                        <Stack direction="row" spacing={1} sx={{ pt: 0.75, pb: 0.5, flexWrap: "wrap" }}>
                            {!context?.graphEnabled && <Typography fontSize={12}>Befragte Dokumente:</Typography>}

                            {context?.companyContextEnabled && (
                                <Grid2>
                                    <DocChip
                                        chipType="company"
                                        size="tiny"
                                        truncateLabel={true}
                                        showTooltip={true}
                                        tooltipTitle="Diese Nachricht wurde anhand des Firmenprofil generiert."
                                    />
                                </Grid2>
                            )}

                            {context?.graphEnabled && (
                                <Grid2>
                                    <DocChip
                                        chipType="graph"
                                        size="tiny"
                                        truncateLabel={true}
                                        showTooltip={true}
                                        tooltipTitle="Diese Nachricht wurde im Graph-Modus generiert."
                                    />
                                </Grid2>
                            )}

                            {!context?.graphEnabled &&
                                documentRefs.map((node) => (
                                    <Grid2 key={node.id}>
                                        <DocChip node={node} size="tiny" showTooltip={true} />
                                    </Grid2>
                                ))}
                            
                            {usedAttachments.map((att: any) => (
                                <Grid2 key={att.id}>
                                    <DocChip 
                                        chipType="attachment"
                                        fileName={att.fileName}
                                        fileSize={att.size}
                                        size="tiny" 
                                        truncateLabel={true}
                                        showTooltip={true} 
                                    />
                                </Grid2>
                            ))}
                        </Stack>
                    )}
                </Typography>

                <Stack direction={"row"}>
                    {!hideCopy && !isSyntheticMessage && (
                        <IconButton size="small" onClick={handleCopy} sx={{}} disabled={isGenerating}>
                            <icon.copy sx={{ fontSize: 15, color: "semantic.icon.functional" }} />
                        </IconButton>
                    )}

                    {!hideDelete && (
                        <IconButton size="small" onClick={isSyntheticMessage ? handleDelete : () => setIsDeleteDialogOpen(true)} sx={{ pr: 1.5 }} disabled={isGenerating}>
                            <icon.delete sx={{ fontSize: 15, color: "semantic.icon.functional" }} />
                        </IconButton>
                    )}
                </Stack>
            </Stack>
        );
    };

    const MessageContent = () => {
        if (messageIsGenerating) {
            return (
                <Stack direction={"row"} spacing={2} alignItems="center" p={2}>
                    <CircularProgress size={20} color={"secondary"} />
                    <Typography color="semantic.text.paragraph">Antwort wird generiert...</Typography>
                </Stack>
            );
        } else {
            return (
                <>
                    <Card
                        sx={{
                            px: 2,
                            py: 1.25,
                            pt: isBot ? 1.5 : undefined,
                            borderBottomRightRadius: isBot ? undefined : 0,
                            borderBottomLeftRadius: isBot ? (isGraphMessage ? undefined : 0) : undefined,
                            bgcolor: isBot ? "semantic.chat.bot.primary" : "semantic.chat.user.primary",
                        }}
                    >
                        <Stack>
                            {/* Display attachments for user messages (multiple attachments support) */}
                            {!isBot && message.attachments && message.attachments.length > 0 && (
                                <Box mb={1.5}>
                                    <Stack spacing={1}>
                                        {message.attachments
                                            .filter((att): att is NonNullable<typeof att> => att !== null && att !== undefined)
                                            .map((att, index) => (
                                            <AttachmentPreview
                                                key={att.id || index}
                                                fileName={att.fileName}
                                                size={att.fileSize}
                                                tokens={att.tokens}
                                                pages={1}
                                                variant="dark"
                                                mode="message"
                                                onRemove={!isSyntheticMessage && att.id ? () => handleRemoveAttachment(att.id!) : undefined}
                                                disabled={isGenerating}
                                            />
                                        ))}
                                    </Stack>
                                </Box>
                            )}

                            {isBot && context?.graphEnabled && (
                                <Box mb={1.5}>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            backgroundColor: "semantic.highlight.secondary",
                                            color: "semantic.text.paragraph",
                                            padding: 1,
                                            paddingLeft: 2,
                                            borderRadius: 1,
                                            borderLeft: `6px solid ${theme.palette.semantic.highlight.primary}`,
                                        }}
                                    >
                                        Hinweis: Diese Informationen bieten einen Überblick über die relevanten Gesetze. Für detaillierte Informationen muss das jeweilig relevante
                                        Gesetz auf der linken Seite ausgewählt werden.
                                    </Typography>
                                </Box>
                            )}

                            {isBot && message.content ? (
                                <Box>
                                    {/* Verarbeitung des Nachrichteninhalts mit Referenzen */}
                                    {processMessageContent(message.content)}
                                </Box>
                            ) : (
                                <MarkdownRenderer align="left" isUserMessage={!isBot}>
                                    {message.content || ""}
                                </MarkdownRenderer>
                            )}

                            {isBot && context?.graphEnabled && message.content && (
                                <Box mt={3} mb={1.5}>
                                    {/* Referenzliste der Gesetze als Buttons anzeigen */}
                                    {(() => {
                                        const references = [...(message.content?.matchAll(REFERENCE_PATTERN) || [])];
                                        if (references.length === 0) return null;

                                        // Extract and normalize unique references
                                        const uniqueRefsWithDetails = Array.from(
                                            new Set(
                                                references.map((match) => {
                                                    const reference = match[1].trim();
                                                    const additionalInfo = match[2]?.trim() || "";
                                                    const page = match[3]?.trim() || "1";
                                                    const {
                                                        reference: normalizedRef,
                                                        additionalInfo: normalizedInfo,
                                                        page: normalizedPage,
                                                    } = normalizeReferenceData(reference, additionalInfo, page);
                                                    return { ref: normalizedRef, info: normalizedInfo, page: normalizedPage };
                                                }),
                                            ),
                                        );
                                        
                                        // Filter out non-existent documents
                                        const existingRefs = uniqueRefsWithDetails.filter(
                                            refObj => !nonExistentDocuments.includes(refObj.ref)
                                        );

                                        return (
                                            <Card
                                                sx={{
                                                    backgroundColor: "semantic.highlight.secondary",
                                                    borderLeft: `6px solid ${theme.palette.semantic.highlight.primary}`,
                                                    p: 2,
                                                    boxShadow: "none",
                                                }}
                                            >
                                                <Typography variant="body2" sx={{ fontWeight: 500, mb: 1.5, color: "semantic.text.paragraph" }}>
                                                    Referenzierte Gesetze:
                                                </Typography>
                                                
                                                {nonExistentDocuments.length > 0 && (
                                                    <Typography 
                                                        variant="body2" 
                                                        sx={{ 
                                                            backgroundColor: hex2rgba(theme.palette.semantic.notification.error, 0.1),
                                                            color: theme.palette.semantic.notification.error,
                                                            p: 1,
                                                            borderRadius: 1,
                                                            mb: 1.5
                                                        }}
                                                    >
                                                        Hinweis: Einige der referenzierten Dokumente sind in unserem System nicht verfügbar.
                                                    </Typography>
                                                )}
                                                
                                                {existingRefs.length > 0 ? (
                                                    <Stack direction="row" spacing={0} flexWrap="wrap" sx={{ pl: 0.5, gap: 1 }}>
                                                        {existingRefs.map((refObj, index) => (
                                                            <Button
                                                                key={`refbtn-${index}`}
                                                                variant="contained"
                                                                size="small"
                                                                disableElevation
                                                                onClick={() => selectReferenceClick(refObj.ref, refObj.info, refObj.page)}
                                                                sx={{
                                                                    textTransform: "none",
                                                                    borderRadius: 10,
                                                                    py: 0.5,
                                                                    px: 2,
                                                                    margin: 0,
                                                                    color: "semantic.text.inverted",
                                                                    border: "none",
                                                                    fontWeight: "normal",
                                                                }}
                                                            >
                                                                {refObj.ref}
                                                            </Button>
                                                        ))}
                                                    </Stack>
                                                ) : nonExistentDocuments.length > 0 ? (
                                                    <Typography variant="body2" sx={{ color: "semantic.text.paragraph" }}>
                                                        Leider sind alle referenzierten Dokumente nicht in unserem System verfügbar.
                                                    </Typography>
                                                ) : null}
                                                
                                                {existingRefs.length > 0 && (
                                                    <Typography variant="body2" sx={{ fontWeight: 500, mt: 1.5, color: "semantic.text.paragraph" }}>
                                                        Willst du eines der Gesetze direkt befragen? Klicke es einfach an!
                                                    </Typography>
                                                )}
                                            </Card>
                                        );
                                    })()}
                                </Box>
                            )}
                        </Stack>
                    </Card>
                </>
            );
        }
    };

    return (
        <>
            <Grid2 container size={10} alignItems={"end"} justifySelf={isBot ? undefined : "end"} spacing={1.5}>
                {isBot && !onMobile && (
                    <Grid2>
                        <LogoImageOnly size={30} onClick={undefined} />
                    </Grid2>
                )}

                <Grid2 container size={"grow"} justifyContent={isBot ? "start" : "end"}>
                    <Grid2>
                        <Card sx={{ 
                            border: isGraphMessage ? `2px solid ${theme.palette.semantic.highlight.primary}` : undefined,
                            bgcolor: isBot ? "semantic.chat.bot.secondary" : "semantic.chat.user.secondary",
                        }}>
                            <MessageContent />
                            <MessageContextBar />
                        </Card>
                    </Grid2>
                </Grid2>
            </Grid2>

            <Dialog open={isDeleteDialogOpen} onClose={() => setIsDeleteDialogOpen(false)}>
                <DialogTitle>Nachricht löschen</DialogTitle>
                <DialogContent>Möchtest Du diese Nachricht wirklich löschen?</DialogContent>
                <DialogActions>
                    <Button variant={"outlined"} onClick={() => setIsDeleteDialogOpen(false)}>
                        Abbrechen
                    </Button>
                    <Button variant={"contained"} onClick={handleDelete} color="error">
                        Löschen
                    </Button>
                </DialogActions>
            </Dialog>

            <DocumentDialog
                open={!!openedDocument}
                onClose={() => setOpenedDocument(null)}
                documentReference={openedDocument?.reference}
                additionalInfo={openedDocument?.additionalInfo}
                page={openedDocument?.page}
            />
        </>
    );
};

export default ChatMessage;
